import React, {FC, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import {getDebug} from '../../../library/GeneralUse';
import {readNewsDocumentsLimit} from '../../../library/NewsCollection';
import {useNewsStore} from '../../../stores/NewsStore';

const style = {
	container: `relative   pb-4 pr-4   min-h-48 recommended:min-h-96   max-h-72 recommended:max-h-[27rem]`,
	scrollContainer: `flex flex-col gap-y-4   pr-4   h-full   overflow-y-scroll   scrollbar-webkit`,
	gradientOverlay: `absolute left-0 bottom-4 right-4   h-24   bg-gradient-to-b from-transparent to-whirlwindDarkest`,
};

type NewsContainerProps = {
	children?: ReactNode;
	dataTestId: string;
	id: string;
};

const DEBUG = getDebug('news');

export const NewsContainer: FC<NewsContainerProps> = ({
	children,
	dataTestId,
	id,
}) => {
	// Zustand
	const news = useNewsStore((state) => state.news);

	// Component variables
	const [showGradient, setShowGradient] = useState(false);
	const newsContainerRef = useRef<HTMLDivElement>(null);

	// Component methods
	const handleScroll = async () => {
		try {
			if (newsContainerRef.current) {
				const scrollHeight = newsContainerRef.current.scrollHeight;
				const scrollTop = newsContainerRef.current.scrollTop;
				const clientHeight = newsContainerRef.current.clientHeight;

				// This is used to determine if we hit the bottom of the newsContainer i.e. the overflow/scroll container for NewsPosts.
				// How it works https://stackoverflow.com/questions/45585542/detecting-when-user-scrolls-to-bottom-of-div-with-react-js
				const bottom =
					Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1;

				if (bottom) {
					DEBUG && console.log('NEWS BOTTOM REACHED!', bottom);

					const moreNewsLoaded = await readNewsDocumentsLimit(4);
					DEBUG && console.log('moreNewsLoaded', moreNewsLoaded);
					if (!moreNewsLoaded) {
						setShowGradient(false);
					}
				} else {
					setShowGradient(true);
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	useLayoutEffect(() => {
		if (news && newsContainerRef.current) {
			const scrollHeight = newsContainerRef.current.scrollHeight;
			const clientHeight = newsContainerRef.current.clientHeight;

			setShowGradient(!(scrollHeight === clientHeight));
		}
	}, [news]);

	return (
		<div className={style.container}>
			<div
				className={style.scrollContainer}
				data-test-id={dataTestId}
				id={id}
				onScroll={async () => {
					await handleScroll();
				}}
				ref={newsContainerRef}>
				{children}
				{showGradient ? <div className={style.gradientOverlay} /> : <></>}
			</div>
		</div>
	);
};
